import axios from "axios";

// Configuration de base
const API_URL = "/api/mollie";

// Client axios configuré
const mollieClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 10000,
});

// Cache pour stocker les méthodes de paiement
let paymentMethodsCache = null;
let cacheTimestamp = null;
const CACHE_VALIDITY = 5 * 60 * 1000; // 5 minutes en millisecondes

/**
 * Génère un identifiant de commande unique
 * @returns {string} Identifiant de commande unique
 */
const generateOrderId = () => {
  const date = new Date();
  const dateStr = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("");
  const randomChars = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `BP-${dateStr}-${randomChars}`;
};

/**
 * Nettoie et valide les options de requête pour les méthodes de paiement
 * @param {Object} options - Options de requête
 * @returns {Object} Options nettoyées
 */
const cleanMethodOptions = (options = {}) => {
  const { amount, currency, locale, resource, billingCountry, ...rest } =
    options;

  const cleanedOptions = {
    ...rest,
    locale: locale || "fr_BE",
  };

  if (amount && currency) {
    cleanedOptions.amount = {
      value: parseFloat(amount).toFixed(2),
      currency: currency || "EUR",
    };
  }

  return cleanedOptions;
};

/**
 * Récupère les méthodes de paiement disponibles depuis l'API Mollie
 * @param {Object} options - Options de filtrage
 * @returns {Promise<Array>} Liste des méthodes de paiement
 */
const getPaymentMethods = async (options = {}) => {
  // Vérifier le cache d'abord
  const now = Date.now();
  if (
    paymentMethodsCache &&
    cacheTimestamp &&
    now - cacheTimestamp < CACHE_VALIDITY
  ) {
    return paymentMethodsCache;
  }

  try {
    const cleanedOptions = cleanMethodOptions(options);
    const response = await mollieClient.get("/methods", {
      params: cleanedOptions,
    });

    if (!response?.data?._embedded?.methods) {
      throw new Error("Format de réponse inattendu de l'API Mollie");
    }

    const methods = response.data._embedded.methods.map((method) => ({
      id: method.id,
      name: method.description || method.name,
      image: method.image?.size1x || method.image?.size2x || null,
      issuers: method.issuers || [],
      pricing: method.pricing,
      minimumAmount: method.minimumAmount,
      maximumAmount: method.maximumAmount,
    }));

    // Mise à jour du cache
    paymentMethodsCache = methods;
    cacheTimestamp = now;

    return methods;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des méthodes de paiement:",
      error
    );
    throw new Error(
      error.response?.data?.detail ||
        "Impossible de récupérer les méthodes de paiement. Veuillez réessayer."
    );
  }
};

/**
 * Nettoie et formate les données de paiement pour l'API Mollie
 * @param {Object} paymentData - Données de paiement brutes
 * @returns {Object} Données de paiement formatées
 */
const cleanPaymentData = (paymentData) => {
  const {
    amount,
    currency,
    expiresAt,
    profileId,
    redirectUrl,
    metadata,
    ...rest
  } = paymentData;

  const cleanedData = { ...rest };

  // Gestion du montant
  if (typeof amount === "number" || typeof amount === "string") {
    cleanedData.amount = {
      currency: currency || "EUR",
      value: parseFloat(amount).toFixed(2),
    };
  } else if (amount && typeof amount === "object") {
    cleanedData.amount = {
      currency: amount.currency || currency || "EUR",
      value: parseFloat(amount.value).toFixed(2),
    };
  }

  // Gestion des métadonnées
  if (metadata) {
    cleanedData.metadata = {
      ...metadata,
      // S'assurer qu'il y a un orderId dans les métadonnées
      orderId: metadata.orderId || generateOrderId(),
    };
  } else {
    cleanedData.metadata = {
      orderId: generateOrderId(),
    };
  }

  // Gestion de l'URL de redirection
  if (redirectUrl) {
    const url = new URL(redirectUrl);
    if (!url.searchParams.has("orderId")) {
      url.searchParams.set("orderId", cleanedData.metadata.orderId);
    }
    if (!url.searchParams.has("origin")) {
      url.searchParams.set("origin", "redirect");
    }
    cleanedData.redirectUrl = url.toString();
  }

  return cleanedData;
};

/**
 * Crée un nouveau paiement via l'API Mollie
 * @param {Object} paymentData - Données de paiement
 * @returns {Promise<Object>} Réponse de l'API Mollie
 */
const createPayment = async (paymentData) => {
  try {
    const cleanedPaymentData = cleanPaymentData(paymentData);

    // Stockage du timestamp pour vérification d'expiration
    localStorage.setItem("paymentStartTime", Date.now().toString());

    const response = await mollieClient.post("/payments", cleanedPaymentData);

    // Stocker les informations pour vérification ultérieure
    if (response.data?.id) {
      localStorage.setItem("molliePaymentId", response.data.id);
      localStorage.setItem(
        "currentOrderId",
        cleanedPaymentData.metadata.orderId
      );
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création du paiement:", error);
    throw new Error(
      error.response?.data?.detail ||
        "Impossible de créer le paiement. Veuillez réessayer."
    );
  }
};

/**
 * Vérifie le statut d'un paiement
 * @param {string} paymentId - ID du paiement
 * @returns {Promise<Object>} Statut du paiement
 */
const verifyPaymentStatus = async (paymentId) => {
  if (!paymentId) {
    throw new Error("Identifiant de paiement non valide");
  }

  try {
    const response = await mollieClient.get(`/payments/${paymentId}`);
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la vérification du statut du paiement:",
      error
    );

    // Si l'erreur indique que le paiement est expiré (code 410)
    if (error.response?.status === 410) {
      return { status: "expired" };
    }

    throw new Error(
      error.response?.data?.detail ||
        "Impossible de vérifier le statut du paiement."
    );
  }
};

/**
 * Vérifie si un paiement est probablement expiré
 * @param {string} startTimeString - Timestamp de début
 * @param {number} expirationMinutes - Durée d'expiration en minutes (défaut: 15)
 * @returns {boolean} True si le paiement est probablement expiré
 */
const isPaymentLikelyExpired = (startTimeString, expirationMinutes = 15) => {
  if (!startTimeString) return false;

  try {
    const startTime = parseInt(startTimeString);
    const expirationTime = startTime + expirationMinutes * 60 * 1000;
    return Date.now() > expirationTime;
  } catch (error) {
    console.error("Erreur lors de la vérification de l'expiration:", error);
    return false;
  }
};

/**
 * Vérifie le statut complet d'un paiement avec gestion de l'expiration
 * @param {string} [paymentId] - ID du paiement (optionnel, récupéré du localStorage si absent)
 * @returns {Promise<Object>} Statut du paiement
 */
const checkPaymentStatus = async (paymentId) => {
  try {
    // Si aucun ID de paiement n'est fourni, vérifier le localStorage
    const storedPaymentId =
      paymentId || localStorage.getItem("molliePaymentId");
    if (!storedPaymentId) {
      return { status: "unknown", message: "Aucun ID de paiement trouvé" };
    }

    // Vérifier si le paiement est potentiellement expiré
    const startTime = localStorage.getItem("paymentStartTime");
    if (isPaymentLikelyExpired(startTime)) {
      return { status: "expired" };
    }

    // Essayer de récupérer le statut du paiement
    return await verifyPaymentStatus(storedPaymentId);
  } catch (error) {
    console.error("Erreur lors de la vérification du statut:", error);
    return { status: "error", message: error.message };
  }
};

// Service Mollie exporté
const mollieService = {
  generateOrderId,
  getPaymentMethods,
  createPayment,
  verifyPaymentStatus,
  checkPaymentStatus,
  isPaymentLikelyExpired,
};

export default mollieService;
